// src/components/report/manifestTracking/ManifestTimeline.js
import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import {
    Flag as FlagIcon,
    CheckCircle as CheckIcon,
    PlayArrow as PlayIcon,
    Place as PlaceIcon,
} from '@mui/icons-material';
import { formatDateTime, formatDate } from '../../../utils/formatters';

const TimelineItem = ({ icon, color, title, subtitle, time, isLast }) => (
    <Box sx={{ display: 'flex', gap: 2 }}>
        <Stack alignItems="center" sx={{ minWidth: 36 }}>
            <Box
                sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    display: 'grid', placeItems: 'center',
                    bgcolor: color, color: '#fff',
                    boxShadow: 1,
                }}
            >
                {icon}
            </Box>
            {!isLast && (
                <Box sx={{ flex: 1, width: 2, bgcolor: 'divider', minHeight: 32 }} />
            )}
        </Stack>

        <Box sx={{ pb: isLast ? 0 : 2.5, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" fontWeight={900}>{title}</Typography>
                {time && <Chip size="small" label={time} variant="outlined" sx={{ fontWeight: 700 }} />}
            </Stack>
            {subtitle && (
                <Typography variant="caption" color="text.secondary" display="block">
                    {subtitle}
                </Typography>
            )}
        </Box>
    </Box>
);

const buildEvents = (data) => {
    if (!data) return [];

    const events = [];

    events.push({
        key: 'plan',
        icon: <FlagIcon fontSize="small" />,
        color: '#607d8b',
        title: 'Manifiesto planificado',
        subtitle: `Salida planeada: ${formatDate(data.manfec)}`,
    });

    if (data.check_ini_id) {
        const parts = [`${data.check_ini_nombre || 'N/A'}`];
        if (data.check_ini_km != null) parts.push(`KM ${data.check_ini_km}`);
        if (data.check_ini_comb != null) parts.push(`Combustible ${data.check_ini_comb}%`);
        events.push({
            key: 'check-ini',
            icon: <CheckIcon fontSize="small" />,
            color: '#2e7d32',
            title: 'Check INICIAL',
            subtitle: parts.join(' · '),
            time: formatDateTime(data.check_ini_fecha),
        });
    }

    const paradas = data.paradas || [];
    paradas.forEach((p) => {
        const isOrigin = p.tipo === 'ORIGEN';
        const isDest = p.tipo === 'DESTINO';
        const icon = isOrigin ? <PlayIcon fontSize="small" />
            : isDest ? <FlagIcon fontSize="small" />
                : <PlaceIcon fontSize="small" />;
        const color = p.visitada ? (isDest ? '#c62828' : '#1976d2') : '#b0bec5';
        const subtitle = p.visitada
            ? `Visitada${p.fecha_visita ? '' : ' (sin hora)'}`
            : `Pendiente${p.hor_estimada ? ' · Estimada: ' + formatDateTime(p.hor_estimada) : ''}`;

        events.push({
            key: `parada-${p.man_par_id}`,
            icon,
            color,
            title: `${isOrigin ? 'Salida' : isDest ? 'Llegada' : 'Parada'} · ${p.nombre || 'N/A'}`,
            subtitle,
            time: p.fecha_visita ? formatDateTime(p.fecha_visita) : null,
        });
    });

    if (data.check_fin_id) {
        const parts = [`${data.check_fin_nombre || 'N/A'}`];
        if (data.check_fin_km != null) parts.push(`KM ${data.check_fin_km}`);
        if (data.check_fin_comb != null) parts.push(`Combustible ${data.check_fin_comb}%`);
        events.push({
            key: 'check-fin',
            icon: <CheckIcon fontSize="small" />,
            color: '#1565c0',
            title: 'Check FINAL',
            subtitle: parts.join(' · '),
            time: formatDateTime(data.check_fin_fecha),
        });
    }

    if (data.manfecfin) {
        events.push({
            key: 'plan-fin',
            icon: <FlagIcon fontSize="small" />,
            color: '#607d8b',
            title: 'Llegada planificada',
            subtitle: formatDate(data.manfecfin),
        });
    }

    return events;
};

const ManifestTimeline = ({ data }) => {
    const events = buildEvents(data);

    return (
        <Box>
            {events.map((ev, idx) => (
                <TimelineItem
                    key={ev.key}
                    icon={ev.icon}
                    color={ev.color}
                    title={ev.title}
                    subtitle={ev.subtitle}
                    time={ev.time}
                    isLast={idx === events.length - 1}
                />
            ))}
        </Box>
    );
};

export default ManifestTimeline;